<template>
  <div class="py-8">
    <v-app-bar flat color="transparent">
      <app-bar-nav-icon />
      <v-toolbar-title class="breadcrumbs d-flex align-center">
        <v-icon class="text-light-blue">{{ icons.arrowLeft }}</v-icon>
        <router-link class="text-decoration--none" :to="{ name: 'categories' }"
          >Categories</router-link
        >
      </v-toolbar-title>
    </v-app-bar>

    <div class="ma-4" v-if="form">
      <div class="display-2 font-weight-bold my-6 text-capitalize">
        {{ displayName }}
      </div>

      <div class="d-flex my-6">
        <v-tabs hide-slider class="user-info-tabs">
          <v-tab class="pa-0" v-for="(item, key) in tabItems" :key="key">
            {{ item.tab }}
          </v-tab>

          <!-- Category Detail Form -->
          <v-tab-item class="col-md-6 col-sm-12 col-xs-12 mt-6 pa-0">
            <image-form
              v-if="category"
              :image_url="category.image_url"
              type="image"
              ratio="2"
              @change="imageChange"
              :avatarLoading="avatarLoading"
            />

            <v-form ref="form" class="category-form">
              <v-divider></v-divider>

              <h3 class="py-5 primary--text">Category Details</h3>

              <label class="text-field-label">Label</label>
              <v-text-field
                flat
                solo
                required
                class="mt-2"
                v-model="form.label"
                :error-messages="form.$getError('label')"
                :loading="loading"
              ></v-text-field>

              <label class="text-field-label">Description</label>
              <v-textarea
                flat
                solo
                required
                class="mt-2"
                v-model="form.description"
                :error-messages="form.$getError('description')"
                :loading="loading"
              ></v-textarea>

              <label class="text-field-label">Icon</label>
              <image-form
                v-if="category"
                :image_url="category.icon_url"
                type="icon"
                ratio="1"
                label="Upload Icon"
                @change="imageChange"
                :avatarLoading="avatarLoading"
              />

              <div class="mt-2">
                <v-btn
                  color="primary"
                  class="mr-4 px-6"
                  height="40px"
                  width="100%"
                  @click="updateCategoryDetails"
                  :loading="form.$busy"
                  >{{ labelUpdate }}</v-btn
                >
              </div>
              <div class="mt-2">
                <v-btn
                  color="danger"
                  class="mr-4 px-6"
                  height="40px"
                  width="100%"
                  @click="onClickDelete"
                  >{{ labelCancel }}</v-btn
                >
              </div>
            </v-form>
          </v-tab-item>

          <!-- Sub Category -->
          <v-tab-item class="col-md-4 col-sm-12 col-xs-12 mt-6 pa-0">
            <v-divider></v-divider>
            <div class="pl-4 mt-5 mb-5">
              <v-row>
                <v-btn color="primary" depressed @click="onClickNewSubCategory">
                  Add New
                </v-btn>
              </v-row>
            </div>
            <v-spacer> </v-spacer>
            <div v-for="(category, index) in sub_categories" :key="category.id">
              <v-card
                class="mx-auto pt-1 pb-1 pr-1 pl-1"
                max-width="auto"
                outlined
              >
                <v-list-item three-line>
                  <v-list-item-content>
                    <v-list-item-title>
                      <p class="mb-1">
                        ID: <strong>{{ category.id }}</strong>
                      </p>
                      <p class="mb-1">
                        Label: <strong>{{ category.label }}</strong>
                      </p>
                      <p class="mb-1">
                        Action:
                        <v-btn
                          elevation="1"
                          x-small
                          class="mr-2"
                          @click="onSelectEditSubCategory(index, category)"
                          >Modify</v-btn
                        >
                        <v-btn
                          elevation="1"
                          x-small
                          color="error"
                          @click="onSelectDeleteSubCategory(category)"
                          >Delete</v-btn
                        >
                      </p>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-card>
              <br />
            </div>

            <!-- add new sub category dialog -->
            <v-dialog v-model="addNewSubCategoryDialog" max-width="290">
              <v-card>
                <v-card-title class="headline">{{
                  subCategoryModalLabel
                }}</v-card-title>
                <v-card-text>
                  <label class="text-field-label">Label</label>
                  <v-text-field
                    flat
                    solo
                    required
                    class="mt-2"
                    v-model="subCategoryForm.label"
                    :error-messages="subCategoryForm.$getError('label')"
                  ></v-text-field>
                </v-card-text>

                <v-card-actions>
                  <v-spacer></v-spacer>
                  <v-btn text @click="addNewSubCategoryDialog = false"
                    >Cancel</v-btn
                  >
                  <v-btn
                    color="red"
                    text
                    @click="onAddNewSubCategory"
                    :loading="addNewSubCategoryLoading"
                    >Save</v-btn
                  >
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-tab-item>
        </v-tabs>
      </div>
    </div>

    <v-overlay absolute :value="loading" opacity="0.2">
      <v-progress-circular
        indeterminate
        color="primary"
        size="64"
      ></v-progress-circular>
    </v-overlay>

    <v-snackbar v-model="snackbar.show" right :color="snackbar.color">
      {{ snackbar.message }}
      <v-btn text @click="snackbar.show = false">OK</v-btn>
    </v-snackbar>

    <!-- delete parent category dialog -->
    <v-dialog v-model="deleteDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">Delete ?</v-card-title>
        <v-card-text
          >Are you sure you want to delete this account ?</v-card-text
        >
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn
            color="red"
            text
            @click="deleteCategory"
            :loading="deleteLoading"
            >Yes, Delete please</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmModal
      v-model="deleteSubCategoryDialog"
      title="Delete Sub Category"
      :message="deleteSubCategoryDialogMessage"
      @cancel="deleteSubCategoryDialog = false"
      @confirm="deleteSubCategory"
    ></ConfirmModal>
  </div>
</template>

<script>
import {
  mdiChevronLeft,
  mdiCheck,
  mdiAlertCircleOutline,
  mdiDelete,
} from '@mdi/js'
import { mapState, mapActions, mapMutations } from 'vuex'
import AppBarNavIcon from '@/layouts/shared/AppBarNavIcon'
import ImageForm from '@/views/Home/Categories/components/ImageForm'
import ConfirmModal from '@/components/modals/ConfirmModal'

import Form from '@/utils/form'

export default {
  name: 'UserDetails',

  components: {
    ImageForm,
    AppBarNavIcon,
    ConfirmModal,
  },

  data() {
    return {
      icons: {
        arrowLeft: mdiChevronLeft,
        check: mdiCheck,
        alert: mdiAlertCircleOutline,
        delete: mdiDelete,
      },
      loading: false,
      avatarLoading: false,
      deleteDialog: false,
      deleteLoading: false,

      deleteSubCategoryDialog: false,
      deleteSubCategoryDialogMessage: '',
      deleteSubCategoryLoading: false,
      addNewSubCategoryDialog: false,
      addNewSubCategoryLoading: false,
      subCategoryModalLabel: '',
      subCategoryEditIndex: null,
      form: new Form(),
      subCategoryForm: new Form(),

      showCropperModal: false,
      avatarTimestamp: new Date().getTime(),
      tabItems: [{ tab: 'Detail' }, { tab: 'Sub Categories' }],
      snackbar: {
        show: false,
        message: null,
        color: '',
      },
      sub_categories: [],
      selectedSubCategory: null,
      showModal: false,
      showStripeResetModal: false,
      panel: [0, 1],
      formMode: this.$route.params.id === 'new' ? 'new' : 'details',
      labelUpdate: this.$route.params.id === 'new' ? 'Save' : 'Update',
      labelCancel: this.$route.params.id === 'new' ? 'Cancel' : 'Delete',
    }
  },

  computed: {
    ...mapState({
      category: (state) => state.category.categoryDetails,
    }),

    displayName() {
      return this.formMode === 'new' ? 'Create Category' : this.form.label
    },
    disableMessage() {
      const action = this.user && this.user.blocked_at ? 'unblock' : 'block'
      return `Are you sure you want ${action} this account?`
    },
  },

  methods: {
    ...mapActions({
      getCategoryDetails: 'category/getCategoryDetails',
      updateCategory: 'category/updateCategory',
    }),

    ...mapMutations({
      clearCategoryDetails: 'category/clearCategoryDetails',
    }),

    async getCategory() {
      this.loading = true
      await this.getCategoryDetails(this.$route.params.id)

      if (this.category.type !== 'sub_category') {
        delete this.category.parent_id
      }

      if (this.category.icon_url.indexOf('default.png') > 0) {
        this.category.icon_url = null
      }

      const { data } = await this.$api.get(
        `admin/categories/${this.category.id}/children`
      )

      this.sub_categories = data.data

      this.form = new Form(this.category)
      this.loading = false
    },

    async imageChange(data) {
      this.form.$clearErrors()
      if (data.type === 'icon') {
        this.category.icon_url = URL.createObjectURL(data.image)
        this.form.icon = data.image
      } else {
        this.category.image_url = URL.createObjectURL(data.image)
        this.form.photo = data.image
      }
    },

    async updateCategoryDetails() {
      this.form.$busy = true
      this.form.$clearErrors()

      if (this.formMode === 'details') {
        this.updateCategory({
          id: this.form.id,
          label: this.form.label,
          description: this.form.description,
          photo: this.form.photo,
          icon: this.form.icon,
        })
          .then(() => {
            this.form.$busy = false
            this.showSnackbar(
              'Category details successfully updated!',
              'success'
            )
            this.$router.replace('/categories')
          })
          .catch((err) => {
            this.form.$busy = false
            if (err.response.status === 422) {
              this.form.$setErrors(err.response.data.errors)
            }
          })
      } else {
        let formData = new FormData()

        if (this.form.label) {
          formData.append('label', this.form.label)
        }

        if (this.form.description) {
          formData.append('description', this.form.description)
        }

        if (this.form.photo) {
          formData.append('photo', this.form.photo)
        }

        if (this.form.icon) {
          formData.append('icon', this.form.icon)
        }

        this.sub_categories.forEach((category) => {
          formData.append('sub_categories[]', category.label)
        })

        this.$api
          .post('admin/categories', formData)
          .then((res) => {
            this.form.$busy = false
            this.showSnackbar('Successfully addded a new category', 'success')
            this.$router.replace('/categories')
          })
          .catch((err) => {
            this.form.$busy = false
            if (err.response.status === 422) {
              this.form.$setErrors(err.response.data.errors)
            }

            this.showSnackbar(err.response.data.message, 'error')
          })
      }
    },

    async deleteCategory() {
      this.deleteLoading = true
      this.$api
        .delete(`admin/categories/${this.form.id}`)
        .then((data) => {})
        .finally(() => {
          this.deleteLoading = false
          this.deleteDialog = false
          this.$router.replace('/categories')
        })
    },

    async deleteSubCategory() {
      this.deleteSubCategoryLoading = true

      if (this.formMode === 'details') {
        this.$api
          .delete(`admin/categories/${this.selectedSubCategory.id}`)
          .then((data) => {})
      }

      this.sub_categories.splice(
        this.sub_categories.indexOf(this.selectedSubCategory),
        1
      )
      this.deleteSubCategoryLoading = false
      this.deleteSubCategoryDialog = false
    },

    async onAddNewSubCategory() {
      this.addNewSubCategoryLoading = true

      if (this.formMode === 'details') {
        this.subCategoryForm.parent_id = this.form.id

        if (this.subCategoryEditIndex === null) {
          this.$api
            .post(`admin/categories`, this.subCategoryForm)
            .then((res) => {
              this.sub_categories.push(res.data.data)
              this.addNewSubCategoryLoading = false
              this.addNewSubCategoryDialog = false
              this.subCategoryForm = new Form()
            })
            .catch((err) => {
              this.addNewSubCategoryLoading = false
              if (err.response.status === 422) {
                this.subCategoryForm.$setErrors(err.response.data.errors)
              }
            })
        } else {
          this.$api
            .put(
              `admin/categories/children/${this.subCategoryForm.id}`,
              this.subCategoryForm
            )
            .then((res) => {
              this.sub_categories[this.subCategoryEditIndex] = res.data.data
              this.addNewSubCategoryLoading = false
              this.addNewSubCategoryDialog = false
              this.subCategoryForm = new Form()
            })
            .catch((err) => {
              this.addNewSubCategoryLoading = false
              if (err.response.status === 422) {
                this.subCategoryForm.$setErrors(err.response.data.errors)
              }
            })
        }
      } else {
        if (!this.subCategoryForm.label) {
          this.subCategoryForm.$setErrors({
            label: ['The label field is required."'],
          })
          this.addNewSubCategoryLoading = false
          return false
        }

        let find = this.sub_categories.find(
          (category) => category.label === this.subCategoryForm.label
        )

        if (find && this.subCategoryEditIndex === null) {
          this.subCategoryForm.$setErrors({
            label: ['Subcategory is already existed'],
          })
        } else {
          if (this.subCategoryEditIndex !== null) {
            this.sub_categories[this.subCategoryEditIndex] = {
              label: this.subCategoryForm.label,
            }
          } else {
            this.sub_categories.push({ label: this.subCategoryForm.label })
          }

          this.subCategoryForm = new Form()
          this.addNewSubCategoryDialog = false
        }

        this.addNewSubCategoryLoading = false
      }
    },

    onSelectDeleteSubCategory(category) {
      this.selectedSubCategory = category

      this.deleteSubCategoryDialog = true
      this.deleteSubCategoryDialogMessage = `Are you sure you want to delete ${this.selectedSubCategory.label}?`
    },

    onSelectEditSubCategory(index, category) {
      this.addNewSubCategoryDialog = true
      this.subCategoryForm = new Form(category)
      this.subCategoryModalLabel = 'Edit ' + this.subCategoryForm.label
      this.subCategoryEditIndex = index
    },

    onClickNewSubCategory() {
      this.addNewSubCategoryDialog = true
      this.subCategoryModalLabel = 'Add new sub category'
      this.subCategoryForm = new Form()
      this.subCategoryEditIndex = null
    },

    onClickDelete() {
      if (this.formMode === 'details') {
        this.deleteDialog = true
      } else {
        this.$router.replace('/categories')
      }
    },

    showSnackbar(message, color) {
      this.snackbar.message = message
      this.snackbar.show = true
      this.snackbar.color = color
    },
  },

  created() {
    this.clearCategoryDetails()

    if (this.formMode === 'details') {
      this.getCategory()
    } else {
      this.clearCategoryDetails()
    }
  },

  watch: {
    $route() {
      this.getCategory()
    },
  },
}
</script>

<style lang="scss" scoped>
.user-form {
  width: 400px;

  @media (max-width: 420px) {
    width: 100%;
  }
}
</style>
